import type { AIService } from '@/lib/supabase-client'
import type { TemplateConfig } from '@/types/template-config'

/**
 * 生成 i18n messages 部分
 */
function generateI18nMessages(config: TemplateConfig): string {
  const messages: Record<string, Record<string, string>> = {}

  config.languages.forEach(lang => {
    messages[lang] = {}
  })

  // 收集所有需要翻译的字段
  const allFields = [...config.formItems, ...config.resultProperties]

  allFields.forEach(field => {
    messages['zh-CN'][field.key] = field.labelZhCN
    messages['en-US'][field.key] = field.labelEnUS
    if (config.languages.includes('ja-JP')) {
      messages['ja-JP'][field.key] = field.labelJaJP
    }
  })

  return JSON.stringify(messages, null, 8).replace(/^/gm, '      ')
}

/**
 * 生成 formItems 部分
 */
function generateFormItems(config: TemplateConfig): string {
  return config.formItems.map(item => `    {
      key: '${item.key}',
      label: t('${item.key}'),
      component: FieldComponent.${item.component},
      props: {
        supportType: [${item.supportType.map(t => `FieldType.${t}`).join(', ')}],
      },
      validator: {
        required: ${item.required || false},
      }
    }`).join(',\n')
}

/**
 * 生成 resultType properties 部分
 */
function generateResultProperties(config: TemplateConfig): string {
  return config.resultProperties.map(prop => {
    const attrs: string[] = []
    attrs.push(`key: '${prop.key}'`)
    if (prop.hidden) attrs.push('isGroupByKey: true')
    attrs.push(`type: FieldType.${prop.fieldType}`)
    attrs.push(`label: ${prop.key === 'id' ? "'id'" : `t('${prop.key}')`}`)
    if (prop.hidden) attrs.push('hidden: true')
    if (prop.primary) attrs.push('primary: true')

    return `        {
          ${attrs.join(',\n          ')}
        }`
  }).join(',\n')
}

/**
 * 生成飞书字段捷径代码模板
 * 基于服务类型（coze-workflow / coze-agent）生成不同的代码
 */
export function generateFieldShortcutCode(
  service: AIService,
  _apiKey: string,
  config: TemplateConfig
): string {
  const serviceType = service.provider
  const serviceName = service.name
  const serviceId = service.id

  // 从环境变量获取 base URL，如果没有则使用默认值
  const baseUrl = import.meta.env.VITE_WORKER_URL || 'https://proxy.apimakt.com'

  // 根据服务类型生成不同的代码模板
  // 注意：为了安全，API Key 在模板中留空，需要用户手动填写
  if (serviceType === 'coze-workflow') {
    return generateCozeWorkflowCode(service, serviceId, serviceName, baseUrl, config)
  } else if (serviceType === 'coze-agent') {
    return generateCozeAgentCode(service, serviceId, serviceName, baseUrl, config)
  }

  return generateGenericCode(service, serviceId, serviceName, baseUrl, config)
}

/**
 * 生成 Coze Workflow 类型的代码模板
 */
function generateCozeWorkflowCode(
  service: AIService,
  serviceId: string,
  serviceName: string,
  baseUrl: string,
  config: TemplateConfig
): string {
  const i18nMessages = generateI18nMessages(config)
  const formItems = generateFormItems(config)
  const resultProperties = generateResultProperties(config)

  return `import { basekit, FieldType, field, FieldComponent, FieldCode } from '@lark-opdev/block-basekit-server-api';
const { t } = field;

// ============================================
// 配置项
// ============================================
const API_BASE_URL = '${baseUrl}';
const API_KEY = ''; // 请在此处填写您的 API Key
const SERVICE_ID = '${serviceId}'; // ${serviceName}

// 轮询配置
const POLLING_CONFIG = {
  MAX_RETRIES: 60,        // 最大重试次数
  RETRY_INTERVAL: 2000,   // 每次轮询间隔（毫秒）
};

// 添加域名白名单
const feishuDm = ['feishu.cn', 'feishucdn.com', 'larksuitecdn.com', 'larksuite.com'];
basekit.addDomainList([...feishuDm, 'proxy.apimakt.com']);

// ============================================
// 字段捷径定义
// ============================================
basekit.addField({
  // 定义捷径的i18n语言资源
  i18n: {
    messages: ${i18nMessages}
  },

  // 定义捷径的入参
  formItems: [
${formItems}
  ],

  // 定义捷径的返回结果类型
  resultType: {
    type: FieldType.Object,
    extra: {
      icon: {
        light: 'https://lf3-static.bytednsdoc.com/obj/eden-cn/eqgeh7upeubqnulog/chatbot.svg',
      },
      properties: [
${resultProperties}
      ],
    },
  },

  // 执行函数
  execute: async (formItemParams: { input: any }, context) => {
    // 日志函数
    function debugLog(arg: any) {
      console.log(JSON.stringify({
        formItemParams,
        context,
        arg
      }))
    }

    // 检查付费插件权限
    const isNeedPayPack = context?.isNeedPayPack || false;
    const hasQuota = context?.hasQuota || false;

    if (!isNeedPayPack || !hasQuota) {
      debugLog({ error: '权限不足' });
      return { code: FieldCode.Error }
    }

    // 处理输入参数
    const extractTextValue = (value: any): string => {
      if (typeof value === 'string') return value;
      if (value?.text) return value.text;
      if (Array.isArray(value) && value.length > 0) {
        return value[0]?.text || value[0] || '';
      }
      return '';
    };

    const inputValue = extractTextValue(formItemParams.input);

    if (!inputValue || inputValue.trim() === '') {
      return { code: FieldCode.Error }
    }

    try {
      // ============================================
      // 第一步：Execute - 发起异步执行请求
      // ============================================
      debugLog({ step: 1, action: 'execute', input: inputValue });

      const executeResponse = await context.fetch(\`\${API_BASE_URL}/api/v1/proxy/\${SERVICE_ID}\`, {
        method: 'POST',
        headers: {
          'Authorization': \`Bearer \${API_KEY}\`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parameters: {
            parameters: { input: inputValue }
          },
          operation_type: 'execute'
        })
      });

      if (!executeResponse.ok) {
        throw new Error(\`Execute 请求失败: \${executeResponse.status}\`);
      }

      const executeResult = await executeResponse.json();
      debugLog({ step: 2, executeResult });

      if (!executeResult.success || !executeResult.data?.execute_id) {
        throw new Error('无法获取执行ID');
      }

      const executeId = executeResult.data.execute_id;

      // ============================================
      // 第二步：Query - 轮询查询执行结果
      // ============================================
      debugLog({ step: 3, action: 'query_start', executeId });

      let outputData = null;
      let attempts = 0;

      while (attempts < POLLING_CONFIG.MAX_RETRIES) {
        attempts++;

        // 等待后再查询
        if (attempts > 1) {
          await new Promise(resolve => setTimeout(resolve, POLLING_CONFIG.RETRY_INTERVAL));
        }

        const queryResponse = await context.fetch(\`\${API_BASE_URL}/api/v1/proxy/\${SERVICE_ID}\`, {
          method: 'POST',
          headers: {
            'Authorization': \`Bearer \${API_KEY}\`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            parameters: {
              execute_id: executeId
            },
            operation_type: 'query'
          })
        });

        if (!queryResponse.ok) {
          throw new Error(\`Query 请求失败: \${queryResponse.status}\`);
        }

        const queryResult = await queryResponse.json();
        debugLog({ step: 4, attempt: attempts, queryResult });

        if (!queryResult.success) {
          throw new Error('查询失败');
        }

        const executeStatus = queryResult.data?.execute_status;
        outputData = queryResult.data?.output;

        // 执行成功且有输出数据
        if (executeStatus === 'Success' && outputData) {
          debugLog({ step: 5, success: true, attempts });
          break;
        }

        // 执行失败
        if (executeStatus === 'Failed') {
          throw new Error('执行失败');
        }
      }

      if (!outputData) {
        throw new Error(\`轮询超时：\${attempts}次尝试后仍未获取到结果\`);
      }

      // ============================================
      // 第三步：解析输出数据
      // ============================================
      let finalOutput = '';
      try {
        const parsed = JSON.parse(outputData);

        // 根据实际返回结构提取数据
        if (parsed.Output) {
          const secondParsed = JSON.parse(parsed.Output);
          finalOutput = secondParsed.output || secondParsed.Output || outputData;
        } else if (parsed.output) {
          finalOutput = parsed.output;
        } else {
          finalOutput = outputData;
        }
      } catch (e) {
        finalOutput = outputData;
      }

      debugLog({ step: 6, finalOutput });

      // 返回结果
      return {
        code: FieldCode.Success,
        data: {
          id: \`\${Math.random()}\`,
          output: finalOutput,
        }
      }

    } catch (e) {
      console.log('====error', String(e));
      debugLog({ error: String(e) });
      return { code: FieldCode.Error }
    }
  },
});

export default basekit;
`;
}

/**
 * 生成 Coze Agent 类型的代码模板
 */
function generateCozeAgentCode(
  service: AIService,
  serviceId: string,
  serviceName: string,
  baseUrl: string,
  config: TemplateConfig
): string {
  const i18nMessages = generateI18nMessages(config)
  const formItems = generateFormItems(config)
  const resultProperties = generateResultProperties(config)

  return `import { basekit, FieldType, field, FieldComponent, FieldCode } from '@lark-opdev/block-basekit-server-api';
const { t } = field;

// ============================================
// 配置项
// ============================================
const API_BASE_URL = '${baseUrl}';
const API_KEY = ''; // 请在此处填写您的 API Key
const SERVICE_ID = '${serviceId}'; // ${serviceName}

// 轮询配置
const POLLING_CONFIG = {
  MAX_RETRIES: 60,
  RETRY_INTERVAL: 2000,
};

// 添加域名白名单
const feishuDm = ['feishu.cn', 'feishucdn.com', 'larksuitecdn.com', 'larksuite.com'];
basekit.addDomainList([...feishuDm, 'proxy.apimakt.com']);

// ============================================
// 字段捷径定义（Coze Agent 类型）
// ============================================
basekit.addField({
  i18n: {
    messages: ${i18nMessages}
  },

  formItems: [
${formItems}
  ],

  resultType: {
    type: FieldType.Object,
    extra: {
      icon: {
        light: 'https://lf3-static.bytednsdoc.com/obj/eden-cn/eqgeh7upeubqnulog/chatbot.svg',
      },
      properties: [
${resultProperties}
      ],
    },
  },

  execute: async (formItemParams: { input: any }, context) => {
    function debugLog(arg: any) {
      console.log(JSON.stringify({ formItemParams, context, arg }))
    }

    const isNeedPayPack = context?.isNeedPayPack || false;
    const hasQuota = context?.hasQuota || false;

    if (!isNeedPayPack || !hasQuota) {
      debugLog({ error: '权限不足' });
      return { code: FieldCode.Error }
    }

    const extractTextValue = (value: any): string => {
      if (typeof value === 'string') return value;
      if (value?.text) return value.text;
      if (Array.isArray(value) && value.length > 0) {
        return value[0]?.text || value[0] || '';
      }
      return '';
    };

    const inputValue = extractTextValue(formItemParams.input);

    if (!inputValue || inputValue.trim() === '') {
      return { code: FieldCode.Error }
    }

    try {
      // Execute - Coze Agent 使用 chat 操作类型
      debugLog({ step: 1, action: 'execute (chat)', input: inputValue });

      const executeResponse = await context.fetch(\`\${API_BASE_URL}/api/v1/proxy/\${SERVICE_ID}\`, {
        method: 'POST',
        headers: {
          'Authorization': \`Bearer \${API_KEY}\`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parameters: { input: inputValue },
          operation_type: 'chat'
        })
      });

      if (!executeResponse.ok) {
        throw new Error(\`Execute 请求失败: \${executeResponse.status}\`);
      }

      const executeResult = await executeResponse.json();
      debugLog({ step: 2, executeResult });

      if (!executeResult.success || !executeResult.data?.execute_id) {
        throw new Error('无法获取执行ID');
      }

      const executeId = executeResult.data.execute_id;

      // Query - 轮询查询结果
      debugLog({ step: 3, action: 'query_start', executeId });

      let outputData = null;
      let attempts = 0;

      while (attempts < POLLING_CONFIG.MAX_RETRIES) {
        attempts++;

        if (attempts > 1) {
          await new Promise(resolve => setTimeout(resolve, POLLING_CONFIG.RETRY_INTERVAL));
        }

        const queryResponse = await context.fetch(\`\${API_BASE_URL}/api/v1/proxy/\${SERVICE_ID}\`, {
          method: 'POST',
          headers: {
            'Authorization': \`Bearer \${API_KEY}\`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            parameters: {
              execute_id: executeId
            },
            operation_type: 'query'
          })
        });

        if (!queryResponse.ok) {
          throw new Error(\`Query 请求失败: \${queryResponse.status}\`);
        }

        const queryResult = await queryResponse.json();
        debugLog({ step: 4, attempt: attempts, queryResult });

        if (!queryResult.success) {
          throw new Error('查询失败');
        }

        const executeStatus = queryResult.data?.execute_status;
        outputData = queryResult.data?.output;

        if (executeStatus === 'Success' && outputData) {
          debugLog({ step: 5, success: true, attempts });
          break;
        }

        if (executeStatus === 'Failed') {
          throw new Error('执行失败');
        }
      }

      if (!outputData) {
        throw new Error(\`轮询超时：\${attempts}次尝试后仍未获取到结果\`);
      }

      // 解析输出
      let finalOutput = '';
      try {
        const parsed = JSON.parse(outputData);
        finalOutput = parsed.output || parsed.Output || outputData;
      } catch (e) {
        finalOutput = outputData;
      }

      debugLog({ step: 6, finalOutput });

      return {
        code: FieldCode.Success,
        data: {
          id: \`\${Math.random()}\`,
          output: finalOutput,
        }
      }

    } catch (e) {
      console.log('====error', String(e));
      debugLog({ error: String(e) });
      return { code: FieldCode.Error }
    }
  },
});

export default basekit;
`;
}

/**
 * 生成通用类型的代码模板（其他服务类型）
 */
function generateGenericCode(
  service: AIService,
  serviceId: string,
  serviceName: string,
  baseUrl: string,
  config: TemplateConfig
): string {
  const i18nMessages = generateI18nMessages(config)
  const formItems = generateFormItems(config)
  const resultProperties = generateResultProperties(config)

  return `import { basekit, FieldType, field, FieldComponent, FieldCode } from '@lark-opdev/block-basekit-server-api';
const { t } = field;

// ============================================
// 配置项
// ============================================
const API_BASE_URL = '${baseUrl}';
const API_KEY = ''; // 请在此处填写您的 API Key
const SERVICE_ID = '${serviceId}'; // ${serviceName}

// 添加域名白名单
const feishuDm = ['feishu.cn', 'feishucdn.com', 'larksuitecdn.com', 'larksuite.com'];
basekit.addDomainList([...feishuDm, 'proxy.apimakt.com']);

// ============================================
// 字段捷径定义（通用类型）
// ============================================
basekit.addField({
  i18n: {
    messages: ${i18nMessages}
  },

  formItems: [
${formItems}
  ],

  resultType: {
    type: FieldType.Object,
    extra: {
      icon: {
        light: 'https://lf3-static.bytednsdoc.com/obj/eden-cn/eqgeh7upeubqnulog/chatbot.svg',
      },
      properties: [
${resultProperties}
      ],
    },
  },

  execute: async (formItemParams: { input: any }, context) => {
    function debugLog(arg: any) {
      console.log(JSON.stringify({ formItemParams, context, arg }))
    }

    const isNeedPayPack = context?.isNeedPayPack || false;
    const hasQuota = context?.hasQuota || false;

    if (!isNeedPayPack || !hasQuota) {
      return { code: FieldCode.Error }
    }

    const extractTextValue = (value: any): string => {
      if (typeof value === 'string') return value;
      if (value?.text) return value.text;
      if (Array.isArray(value) && value.length > 0) {
        return value[0]?.text || value[0] || '';
      }
      return '';
    };

    const inputValue = extractTextValue(formItemParams.input);

    if (!inputValue || inputValue.trim() === '') {
      return { code: FieldCode.Error }
    }

    try {
      debugLog({ step: 1, action: 'request', input: inputValue });

      const response = await context.fetch(\`\${API_BASE_URL}/api/v1/proxy/\${SERVICE_ID}\`, {
        method: 'POST',
        headers: {
          'Authorization': \`Bearer \${API_KEY}\`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          parameters: { input: inputValue }
        })
      });

      if (!response.ok) {
        throw new Error(\`请求失败: \${response.status}\`);
      }

      const result = await response.json();
      debugLog({ step: 2, result });

      if (!result.success) {
        throw new Error('请求失败');
      }

      const output = result.data?.output || result.data || '';

      return {
        code: FieldCode.Success,
        data: {
          id: \`\${Math.random()}\`,
          output: String(output),
        }
      }

    } catch (e) {
      console.log('====error', String(e));
      debugLog({ error: String(e) });
      return { code: FieldCode.Error }
    }
  },
});

export default basekit;
`;
}
